import Ajv from 'ajv'
import type {
  FlatMetaFieldSchema,
  MetaValueSchema,
  SellerType,
} from '@/config/metadata-schemas.config'

// strict: false so ajv accepts the x- extension keywords
// coerceTypes: true because Ticketure meta values are strings on the wire
const ajv = new Ajv({ coerceTypes: true, strict: false })

export type MetaValidationResult =
  | { valid: true, value: unknown }
  | { valid: false, errors: string[] }

/** The field types stored as a JSON blob rather than a bare scalar */
const isJsonField = (fieldSchema: MetaValueSchema): boolean => (
  fieldSchema.type === 'object' || fieldSchema.type === 'array'
)

/**
 * Parse and validate a raw metadata string against its field schema.
 *
 * Handles the Ticketure wire format: values are always strings. Booleans
 * accept '1'/'0' (Ticketure's convention, see isTruthy) as well as ajv's
 * native 'true'/'false' coercion; numbers are coerced; object and array
 * fields are JSON.parsed before validation, then checked against the
 * `properties`/`items` the schema declares for the blob.
 *
 * @param {MetaValueSchema} fieldSchema - the field's JSON Schema descriptor
 * @param {string} rawValue - the raw string value from the wire
 * @returns {MetaValidationResult} the coerced value, or validation errors
 */
export const parseMetaValue = (
  fieldSchema: MetaValueSchema,
  rawValue: string,
): MetaValidationResult => {
  let candidate: unknown = rawValue

  if (fieldSchema.type === 'boolean' && (rawValue === '1' || rawValue === '0')) {
    candidate = rawValue === '1' ? 'true' : 'false'
  }

  if (isJsonField(fieldSchema)) {
    try {
      candidate = JSON.parse(rawValue)
    } catch {
      return { valid: false, errors: ['value is not valid JSON'] }
    }
  }

  // ajv can only coerce by mutating its argument, so scalars must be wrapped
  const wrapperSchema = {
    type: 'object',
    properties: { value: fieldSchema },
    required: ['value'],
  }
  const data: { value: unknown } = { value: candidate }

  const validate = ajv.compile(wrapperSchema)

  if (validate(data)) {
    return { valid: true, value: data.value }
  }

  /* istanbul ignore next -- ajv always sets errors when validation fails */
  const ajvErrors = validate.errors || []
  const errors = ajvErrors.map((error) => (
    `${error.instancePath} ${error.message}`.trim()
  ))

  return { valid: false, errors }
}

/**
 * List the fields a seller must still fill in before it can go live.
 *
 * A field counts as missing when its `x-requiredFor` includes the seller's
 * type and no value is set for it. Blank and whitespace-only values are
 * treated as unset — Ticketure stores everything as a string, so an empty
 * string is how an untouched key looks on the wire.
 *
 * @param {FlatMetaFieldSchema[]} fieldSchemas - flattened schema for the seller (see flattenSchemas)
 * @param {SellerType} sellerType - which side of the venue the seller serves
 * @param {Record<string, string | undefined>} values - raw values by schema key
 * @returns {FlatMetaFieldSchema[]} the required fields that have no value
 */
export const getMissingRequiredFields = (
  fieldSchemas: FlatMetaFieldSchema[],
  sellerType: SellerType,
  values: Record<string, string | undefined>,
): FlatMetaFieldSchema[] => fieldSchemas.filter((fieldSchema) => (
  fieldSchema['x-requiredFor']?.includes(sellerType)
  && !values[fieldSchema.key]?.trim()
))

/**
 * How a field's current value stands against its schema.
 *
 * `missing` covers both "no entry at all" and "entry with a blank value" —
 * Ticketure stores everything as a string, so an empty string is how an
 * untouched key looks on the wire (same rule as getMissingRequiredFields).
 */
export type MetaFieldStatus = 'missing' | 'valid' | 'invalid'

/** One schema field joined with its current value; what the inspector renders */
export type MetaFieldState = {
  schema: FlatMetaFieldSchema
  /** Raw wire value, or '' when unset */
  value: string
  status: MetaFieldStatus
  /** Validation errors from parseMetaValue; empty unless status is `invalid` */
  errors: string[]
  /** Whether any seller type must have this set before going live */
  required: boolean
}

/** Values are unique per entity + key, which is how the inspector joins them */
const valueKey = (resourceType: string, key: string): string => `${resourceType}:${key}`

/**
 * Join a flattened schema with the metadata values read for a resource, so the
 * management tool can render one row per field regardless of whether a value
 * exists for it.
 *
 * Every schema field yields a state: fields with no value (or a blank one) come
 * back `missing`, the rest are run through parseMetaValue and come back `valid`
 * or `invalid` with the errors to show. Values whose key is absent from the
 * schema are ignored — the schema is the source of truth for what the tool
 * displays.
 *
 * @param {FlatMetaFieldSchema[]} fieldSchemas - flattened schema (see flattenSchemas)
 * @param {MetadataManagementTool.Metadata[]} values - metadata entries read for the resource
 * @returns {MetaFieldState[]} one state per schema field, in schema order
 */
export const getFieldStates = (
  fieldSchemas: FlatMetaFieldSchema[],
  values: MetadataManagementTool.Metadata[],
): MetaFieldState[] => {
  const valuesByKey = new Map(values.map((entry) => (
    [valueKey(entry.resourceType, entry.key), entry.value]
  )))

  return fieldSchemas.map((schema) => {
    const value = valuesByKey.get(valueKey(schema.resourceType, schema.key)) ?? ''
    const required = Boolean(schema['x-requiredFor']?.length)

    if (!value.trim()) {
      return { schema, value: '', status: 'missing', errors: [], required }
    }

    const result = parseMetaValue(schema, value)

    return result.valid
      ? { schema, value, status: 'valid', errors: [], required }
      : { schema, value, status: 'invalid', errors: result.errors, required }
  })
}

/**
 * Resolve the key a field's value must be WRITTEN under.
 *
 * Ticketure fields are stored under `x-rawKey`, never under the camelCase
 * schema key. The distinction matters because it is invisible on read:
 * transformMeta camelCases raw keys with lodash, which splits on `:` and `_`
 * alike, so `slr:tkt:tax_waived_enabled` and a literal `slrTktTaxWaivedEnabled`
 * both read back as `slrTktTaxWaivedEnabled`. Writing under the schema key
 * would therefore create a SECOND row that collides with the real one, and
 * `.find()` order would decide which of the two the app sees — an edit that
 * appears to save and then intermittently does nothing.
 *
 * Every ticketure field in METADATA_SCHEMAS carries a raw key (the
 * MetaFieldSchema union enforces it, and the config spec asserts that it
 * camelCases back to the schema key), so the throw here is unreachable for
 * schema-derived fields. It exists so that a hand-built field, or a schema
 * loaded from somewhere other than the const, fails loudly instead of
 * corrupting data.
 *
 * Edge Config fields have no raw key: the schema key IS the storage key.
 *
 * @param {FlatMetaFieldSchema} fieldSchema - the flattened field descriptor
 * @returns {string} the key to write the value under
 * @throws {Error} when a ticketure field has no x-rawKey
 */
export const getWriteKey = (fieldSchema: FlatMetaFieldSchema): string => {
  if (fieldSchema['x-source'] !== 'ticketure') {
    return fieldSchema.key
  }

  const rawKey = fieldSchema['x-rawKey']

  if (!rawKey) {
    throw new Error(
      `Refusing to write metadata "${fieldSchema.key}" (${fieldSchema.resourceType}): `
      + 'no x-rawKey. Writing under the camelCase key would shadow the real Ticketure key.',
    )
  }

  return rawKey
}

/**
 * Serialize a parsed metadata value back to the Ticketure wire format.
 *
 * @param {MetaValueSchema} fieldSchema - the field's JSON Schema descriptor
 * @param {unknown} value - the parsed value to serialize
 * @returns {string} the string to store in Ticketure
 */
export const serializeMetaValue = (fieldSchema: MetaValueSchema, value: unknown): string => {
  if (fieldSchema.type === 'boolean') {
    return value ? '1' : '0'
  }

  if (isJsonField(fieldSchema)) {
    return JSON.stringify(value)
  }

  return String(value)
}
